import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export type UserStatus = 'pending' | 'approved' | 'rejected';
export type UserRole =
  | 'admin'
  | 'coadmin'
  | 'technician'
  | 'designer'
  | 'pd'
  | 'engineer'
  | 'sales';

export interface UserProfile {
  id: string;
  email: string;
  display_name: string | null;
  role: UserRole;
  status: UserStatus;
  can_write: boolean;
  is_active: boolean;
}

export interface RoleSummary {
  slug: string;
  name: string;
  can_write: boolean;
  can_see_money: boolean;
  can_approve_users: boolean;
  can_run_autoschedule: boolean;
  scope: 'all' | 'assigned_only' | 'sample_only';
}

export interface AuthContextValue {
  session: Session | null;
  email: string | null;
  profile: UserProfile | null;
  roles: RoleSummary[];
  loading: boolean;
  profileError: string | null;
  isApproved: boolean;
  canWrite: boolean;
  isAdmin: boolean;
  isCoAdmin: boolean;
  canApproveUsers: boolean;
  isTechnician: boolean;
  isDesigner: boolean;
  /** Back-compat alias kept for existing call sites. */
  isZidan: boolean;
  isPD: boolean;
  /** Sales user: view-only, restricted to Request a Test + Lab. */
  isSales: boolean;
  canRunAutoScheduler: boolean;
  canSeeMoney: boolean;
  /** True if user has any role with scope='assigned_only' (e.g. Lab Technician). */
  scopedToOwn: boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface RawProfileResult {
  profile: UserProfile | null;
  roles: RoleSummary[];
  error: string | null;
}

async function fetchProfile(userId: string): Promise<RawProfileResult> {
  try {
    const { data, error } = await (supabase as unknown as {
      from: (t: string) => {
        select: (cols: string) => {
          eq: (col: string, val: string) => {
            maybeSingle: () => Promise<{
              data:
                | (UserProfile & {
                    user_roles: { roles: RoleSummary }[];
                  })
                | null;
              error: { message: string } | null;
            }>;
          };
        };
      };
    })
      .from('users')
      .select(
        'id,email,display_name,role,status,can_write,is_active,' +
          'user_roles(roles(slug,name,can_write,can_see_money,can_approve_users,can_run_autoschedule,scope))',
      )
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      // eslint-disable-next-line no-console
      console.error('[useAuth] profile load failed:', error.message);
      return { profile: null, roles: [], error: error.message };
    }
    if (!data) {
      return { profile: null, roles: [], error: null };
    }

    const profile: UserProfile = {
      id: data.id,
      email: data.email,
      display_name: data.display_name,
      role: data.role,
      status: data.status,
      can_write: data.can_write,
      is_active: data.is_active,
    };
    const roles: RoleSummary[] = (data.user_roles ?? [])
      .map((row) => row.roles)
      .filter((r): r is RoleSummary => !!r);
    return { profile, roles, error: null };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    // eslint-disable-next-line no-console
    console.error('[useAuth] profile load failed:', msg);
    return { profile: null, roles: [], error: msg };
  }
}

/** Renew the session on tab focus only when it expires within this window;
 *  otherwise Supabase's own background refresh already keeps it current. */
const RENEW_WITHIN_MS = 5 * 60 * 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // One profile load per person: remember whose profile is loaded, and share
  // a load that is already running instead of starting another.
  const loadedFor = useRef<string | null>(null);
  const inflight = useRef<{ userId: string; p: Promise<RawProfileResult> } | null>(null);

  const loadProfileOnce = useCallback((userId: string, force = false): Promise<RawProfileResult> => {
    if (!force && inflight.current && inflight.current.userId === userId) return inflight.current.p;
    const p = fetchProfile(userId).finally(() => {
      if (inflight.current?.p === p) inflight.current = null;
    });
    inflight.current = { userId, p };
    return p;
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!session?.user?.id) {
      loadedFor.current = null;
      setProfile(null);
      setRoles([]);
      setProfileError(null);
      return;
    }
    const r = await loadProfileOnce(session.user.id, true);
    loadedFor.current = r.profile ? session.user.id : null;
    setProfile(r.profile);
    setRoles(r.roles);
    setProfileError(r.error);
  }, [session, loadProfileOnce]);

  useEffect(() => {
    let mounted = true;

    // Helper: wrap a promise with a timeout so a hung Supabase call
    // doesn't leave us on the loading splash forever.
    function withDeadline<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
      return new Promise((resolve, reject) => {
        const t = setTimeout(() => reject(new Error(label + ' timed out')), ms);
        p.then(
          (v) => { clearTimeout(t); resolve(v); },
          (e) => { clearTimeout(t); reject(e); },
        );
      });
    }

    async function applyProfile(userId: string) {
      try {
        const r = await withDeadline(loadProfileOnce(userId), 12000, 'fetchProfile');
        if (mounted) {
          loadedFor.current = r.profile ? userId : null;
          setProfile(r.profile);
          setRoles(r.roles);
          setProfileError(r.error);
        }
      } catch (e) {
        // Profile fetch hung. Don't sign user out — just show the error
        // so they can hit the Emergency Reset button if they want.
        if (mounted) {
          setProfileError(e instanceof Error ? e.message : String(e));
        }
      }
    }

    (async () => {
      // Session restore with patience: a page refresh right after activity can
      // hit a moment where the network is re-establishing. One slow attempt
      // must NOT log the user out.
      async function getSessionPatiently() {
        try {
          return await withDeadline(supabase.auth.getSession(), 15000, 'getSession');
        } catch {
          // brief pause, then one more try before giving up
          await new Promise((r) => setTimeout(r, 1200));
          return await withDeadline(supabase.auth.getSession(), 15000, 'getSession');
        }
      }
      try {
        const { data } = await getSessionPatiently();
        if (!mounted) return;
        setSession(data.session);
        if (data.session?.user?.id) {
          await applyProfile(data.session.user.id);
        }
      } catch (e) {
        // getSession hung twice. IMPORTANT: do NOT wipe stored auth — the
        // token is almost certainly fine and the network is just slow; wiping
        // here is what used to bounce people to the login page after a
        // refresh. Leaving storage intact means a plain refresh recovers the
        // session. (Truly-dead sessions are still cleaned up by the sign-in
        // flow and the focus handler.)
        // eslint-disable-next-line no-console
        console.warn('[useAuth] session restore slow/failed; keeping stored auth:', e);
        if (mounted) {
          setSession(null);
          setProfile(null);
          setRoles([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange(async (event, s) => {
      if (!mounted) return;
      setSession(s);
      // TOKEN_REFRESHED fires every ~hour as the JWT rotates; INITIAL_SESSION is
      // already handled by the restore above. Same user either way — don't
      // re-fetch the profile, that makes every module flicker through "Loading…".
      if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED' || event === 'INITIAL_SESSION') {
        return;
      }
      if (s?.user?.id) {
        // SIGNED_IN also fires when the tab regains focus. Only a DIFFERENT
        // person (or no profile loaded yet) needs a fresh profile load.
        if (loadedFor.current === s.user.id) return;
        await applyProfile(s.user.id);
      } else {
        loadedFor.current = null;
        setProfile(null);
        setRoles([]);
        setProfileError(null);
      }
    });

    // Tab-focus recovery: if you leave the tab open for hours then come back,
    // make sure the session is still usable instead of relying on background
    // refresh. Only these server responses mean the session is DEFINITIVELY
    // dead (signed out, revoked, or past the project's session time-box /
    // inactivity limits). Anything else (offline, timeout, 5xx) is a network
    // problem — the session is still valid and must never be wiped for it.
    const isAuthDead = (msg: string) =>
      /invalid refresh token|refresh token not found|refresh token has been revoked|already used|session_not_found|session.?not.?found|session_expired|session.?expired/i.test(msg);

    // 'focus' and 'visibilitychange' fire together: run one check at a time,
    // and not more than once every 5 seconds.
    let checking = false;
    let lastCheck = 0;

    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      // If the machine is offline (Wi-Fi off, VPN drop, waking from sleep),
      // don't even try — a refresh can only fail. The next focus/online
      // event will validate once connectivity is back.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      if (checking || Date.now() - lastCheck < 5000) return;
      checking = true;
      lastCheck = Date.now();
      try {
        // Still comfortably valid? Then there is nothing to do: no network call.
        const { data: cur } = await supabase.auth.getSession();
        const expiresAt = (cur.session?.expires_at ?? 0) * 1000;
        if (cur.session && expiresAt - Date.now() > RENEW_WITHIN_MS) return;
        if (!cur.session) return; // signed out already; the login screen handles it

        // refreshSession() actively contacts the server to mint a new JWT
        // using the stored refresh token.
        let { data, error } = await supabase.auth.refreshSession();
        if (error || !data.session) {
          // One transient failure (laptop waking up, brief network blip)
          // should NOT log the user out. Wait a moment and try once more.
          await new Promise((r) => setTimeout(r, 1500));
          ({ data, error } = await supabase.auth.refreshSession());
        }
        if (error || !data.session) {
          const msg = error?.message ?? '';
          if (error && !isAuthDead(msg)) {
            // Network-ish failure: keep the session, keep the user signed in.
            // Data hooks retry on their own once the connection returns.
            // eslint-disable-next-line no-console
            console.warn('[useAuth] refresh failed transiently; keeping session:', msg);
            return;
          }
          // The server said the session is over (signed out elsewhere, revoked,
          // or past the session limits): clear stored auth and go to login.
          // eslint-disable-next-line no-console
          console.warn('[useAuth] session has ended, redirecting to login');
          try {
            for (const store of [window.localStorage, window.sessionStorage]) {
              for (let i = store.length - 1; i >= 0; i--) {
                const k = store.key(i);
                if (k && (k.startsWith('sb-') || k === 'liteline.auth')) {
                  store.removeItem(k);
                }
              }
            }
          } catch { /* ignore */ }
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      } catch (e) {
        // Thrown = network layer failed entirely. Session stays.
        // eslint-disable-next-line no-console
        console.warn('[useAuth] refresh on focus threw (keeping session):', e);
      } finally {
        checking = false;
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [loadProfileOnce]);

  const email = session?.user?.email?.toLowerCase() ?? null;
  const isApproved =
    !!profile && profile.status === 'approved' && profile.is_active;
  const hasSlug = (slug: string) => roles.some((r) => r.slug === slug);
  const anyRoleGrants = (key: keyof RoleSummary): boolean =>
    roles.some((r) => r[key] === true);

  const isAdmin = hasSlug('admin');
  const isCoAdmin = hasSlug('coadmin');
  const isTechnician = hasSlug('lab_technician');
  const isDesigner = hasSlug('designer');
  const isPD = profile?.role === 'pd' || hasSlug('pd');
  // Sales is driven by the users.role column (not the slug/role-grant system).
  const isSales = profile?.role === 'sales';
  const canApproveUsers = isApproved && anyRoleGrants('can_approve_users');
  const canSeeMoney = isApproved && anyRoleGrants('can_see_money');
  const canRunAutoScheduler = isApproved && anyRoleGrants('can_run_autoschedule');
  // canWrite: approved + (user-level flag OR any role grants can_write).
  // Sales is always read-only regardless of other flags.
  // PD and Sales are read-only across modules. PD can still submit in Request a
  // Test (that flow isn't gated by canWrite); Sales can submit there too.
  const canWrite =
    isApproved && !isSales && !isPD && (profile?.can_write === true || anyRoleGrants('can_write'));
  const scopedToOwn = roles.some((r) => r.scope === 'assigned_only');

  const value: AuthContextValue = {
    session,
    email,
    profile,
    roles,
    loading,
    profileError,
    isApproved,
    canWrite,
    isAdmin,
    isCoAdmin,
    canApproveUsers,
    isTechnician,
    isDesigner,
    isZidan: isDesigner,
    isPD,
    isSales,
    canRunAutoScheduler,
    canSeeMoney,
    scopedToOwn,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
